import { useCallback, useEffect, useState } from "react";
import { VERTICALS, FILES } from "../components/shared/data";

/*
 * The share basket, shared by every sales-kit flow.
 *
 * Persisted per-browser in localStorage so a rep does not lose a collected set to a reload.
 * Every read and write is guarded: storage throws in private mode and when site data is
 * blocked, and the basket must keep working in-session even when it cannot be saved.
 * Entries are re-validated against the catalogue on load, so a basket saved before a
 * data.js change can never render a stale row.
 */

const BASKET_KEY = "camcom-one.basket.v1";

export const isValidEntry = (p) => {
  if (!p || typeof p.id !== "string") return false;
  const [base, idx] = p.id.split("#");
  const parts = base.split("|");
  if (parts.length !== 3) return false;
  const [vk, uk, slot] = parts;
  const v = VERTICALS.find((x) => x.key === vk);
  const u = v && v.ucs.find((x) => x.key === uk);
  if (!u || !u.d[slot]) return false;
  if (idx !== undefined) {
    const files = FILES[base];
    if (!files || !files[Number(idx)]) return false;
  }
  return true;
};

const loadBasket = () => {
  try {
    const raw = window.localStorage.getItem(BASKET_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isValidEntry) : [];
  } catch {
    return [];
  }
};

export default function useShareBasket() {
  const [picked, setPicked] = useState(loadBasket);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(BASKET_KEY, JSON.stringify(picked));
    } catch {
      /* private mode, blocked site data or quota — the basket still works this session */
    }
  }, [picked]);

  /* the confirmation line must not outlive the state it described */
  useEffect(() => { setSent(false); }, [picked, email, note]);

  const add = useCallback((id, tKey, sub) => {
    setPicked((prev) =>
      prev.some((p) => p.id === id)
        ? prev
        : [...prev, { id, tKey, s: sub, k: id.split("|")[2].split("#")[0] }]);
  }, []);

  const remove = useCallback((id) => setPicked((prev) => prev.filter((p) => p.id !== id)), []);
  const clearAll = useCallback(() => { setPicked([]); setEmail(""); setNote(""); }, []);
  const has = useCallback((id) => picked.some((p) => p.id === id), [picked]);

  return { picked, add, remove, clearAll, has, email, setEmail, note, setNote, sent, setSent };
}
